import React, { useState } from "react";
import { Open16Regular } from "@fluentui/react-icons";
import { IInstrumentation } from "../../types/IInstrumentation";
import { IArtist } from "../../types/IArtist";
import { IContributorItem } from "../../types/IContributorItem";
import { ITitle } from "../../types/ITitle";
import DisplayTable from "../../../membersportal/sharedComponents/displayTable/DisplayTable";
import IRepertoireComponentDataItem from "../../../redux/types/IRepertoireComponentDataItem";
import { IRepertoireStateKeys } from "../../types/IRepertoireStateKeys";
import { IProductWorkDataItems } from "../../types/IProductWorkDataItems";
import { createCopyOfTableContents } from "../../../membersportal/services/TableFunctions";
import { getLookupValues, mapLookupKey } from "../../../lookup/services/LookupHelpers";
import { IRepertoireField } from "../../types/IRepertoireField";
import { SizedActionButton } from "../../components/actionButton/ActionButton";
import { PRODUCT_TYPE, PRODUCT_WORKS_KEY, RECORDING_TYPE, SEARCH_ACTION, WORK_SEARCH_VIEW, COPY_WORKS_ACTION, PASTE_WORKS_ACTION } from "../../Consts";

export interface IProductMaintenanceWorkTableContainerProps {
    tableContents: IInstrumentation[] | IArtist[] | IContributorItem[] | ITitle[] | IProductWorkDataItems[];
    dataGridTableData: IRepertoireComponentDataItem;
    componentInstance: string;
    openWork: (openItem: IProductWorkDataItems) => void;
    changeData: (value: any, name: IRepertoireStateKeys, objectKey?: string, ipRepresentationsIdsToRemove?: number[]) => void;
    stateKey: string;
    lookupValues: any;
    showModal: any;
    isDataSourceIntray: boolean;
    productType: string;
    copyProductWorks: () => void;
    addCopiedProductWorks: (order: number) => void;
}

export const SIDE_TRACK_SEQUENCE = 'S/T/Sq';

const ProductMaintenanceWorkTableContainer: React.FC<IProductMaintenanceWorkTableContainerProps> = ({
    tableContents,
    dataGridTableData,
    componentInstance,
    stateKey,
    changeData,
    openWork,
    lookupValues,
    showModal,
    isDataSourceIntray,
    productType,
    copyProductWorks,
    addCopiedProductWorks
}) => {
    const MUSIC_USAGE = 'musicUsage';
    const lookup = mapLookupKey(stateKey, MUSIC_USAGE);
    const musicUsageOptions = getLookupValues(lookup, lookupValues);
    const [tableContentsWithSideTrackAndSequence, setTableContentsWithSideTrackAndSequence] = React.useState(tableContents);
    const [filteredTableData, setFilteredTableData] = React.useState(dataGridTableData.fields);
    const [activeSortHeaderSection, setActiveSortHeaderSection] = React.useState<string>("");
    const [ascending, setAscending] = React.useState<boolean>(false);

    const [isCopyDisabled, setIsCopyDisabled] = useState(false);

    const handleCopyProductWorks = () => {
        setIsCopyDisabled(true);
        copyProductWorks();
        setTimeout(() => setIsCopyDisabled(false), 3000);
    };
    
    React.useEffect(() => {
        const tableContentsCopy = createCopyOfTableContents(tableContents);

        const filteredTableData = dataGridTableData.fields.filter(
            (field: IRepertoireField) => {
                switch (productType) {
                    case PRODUCT_TYPE:
                        return field.componentInstance === componentInstance
                            && field.data !== 'Work'
                            && field.name !== 'musicUsage'
                    case RECORDING_TYPE:
                        return field.componentInstance === componentInstance
                            && field.data !== 'Work'
                            && field.name !== 'isrc'
                            && field.name !== 'proprietaryID'
                    default:
                        return field.componentInstance === componentInstance
                            && field.data !== 'Work'
                            && field.name !== 'isrc'
                            && field.name !== 'proprietaryID'
                            && field.name !== 'side'
                            && field.name !== 'track'
                            && field.name !== 'sequence'
                }
            }
        );
        setFilteredTableData(filteredTableData);

        // Preserve current sort after re-renders (e.g., caused by scroll updates)
        if (activeSortHeaderSection) {
            tableContentsCopy.sort((a: any, b: any) => {
                const newA = a[activeSortHeaderSection] ? (a[activeSortHeaderSection].value ? a[activeSortHeaderSection].value : a[activeSortHeaderSection]) || "" : "";
                const newB = b[activeSortHeaderSection] ? (b[activeSortHeaderSection].value ? b[activeSortHeaderSection].value : b[activeSortHeaderSection]) || "" : "";

                // The last applied sort direction is the opposite of the current `ascending` flag
                if (!ascending) {
                    return newA.toString().localeCompare(newB.toString(), undefined, { numeric: true });
                }
                return newB.toString().localeCompare(newA.toString(), undefined, { numeric: true });
            });
        }
        setTableContentsWithSideTrackAndSequence(tableContentsCopy);

    },[ 
        tableContents,
        dataGridTableData,
        componentInstance,
        stateKey,
        changeData,
        openWork,
        lookupValues,
        showModal,
        isDataSourceIntray,
        dataGridTableData.fields
    ])

    const sortTableByColumn = (section: string) => {
        let tableContentsCopy = JSON.parse(JSON.stringify(tableContentsWithSideTrackAndSequence));
        if (activeSortHeaderSection !== section) {
            setAscending(false);
        }
        if (tableContentsCopy) {
            tableContentsCopy.sort((a: any, b: any) => {
                const newA = a[section] ? (a[section].value ? a[section].value : a[section]) || "" : "";
                const newB = b[section] ? (b[section].value ? b[section].value : b[section]) || "" : "";

                if (ascending) {
                    return newA.toString().localeCompare(newB.toString(), undefined, { 'numeric': true });
                }
                return newB.toString().localeCompare(newA.toString(), undefined, { 'numeric': true });
            });

            setActiveSortHeaderSection(section);
            setAscending(!ascending);
            setTableContentsWithSideTrackAndSequence(tableContentsCopy);
        }
    }

    const removeRowAction = (rowItem: any) => {
        const tableContentsCopy = createCopyOfTableContents(tableContents);
        if (tableContentsCopy.indexOf(rowItem) > -1) {
            tableContentsCopy.splice(tableContentsCopy.indexOf(rowItem), 1);
        }
        changeData(tableContentsCopy, stateKey, null, null);
    }

    const rowAction = (openItem: any) => {
        openWork(openItem)
    }

    const onChangeMusicUsageDropDownData = (
        value: any,
        fieldName: string,
        rowIndex: number,
    ) => {
        const tableContentsCopy = createCopyOfTableContents(tableContents);
        tableContentsCopy[rowIndex][fieldName].value = value
        changeData(tableContentsCopy, stateKey);
    };

    const onChangeDurationData = (
        value: any,
        fieldName: string,
        rowIndex: number,
    ) => {
        const tableContentsCopy = createCopyOfTableContents(tableContents);
        tableContentsCopy[rowIndex][fieldName].value = value
        changeData(tableContentsCopy, stateKey);
    };

    const onChangeSequenceData = (
        value: any,
        fieldName: string,
        rowIndex: number,
    ) => {
        const tableContentsCopy = createCopyOfTableContents(tableContents);
        tableContentsCopy[rowIndex][fieldName].value = value
        changeData(tableContentsCopy, stateKey);
    };

    const onChangeOrder = (
        value: any,
        fieldName: string,
        rowIndex: number,
    ) => {
        const tableContentsCopy = createCopyOfTableContents(tableContents);
        tableContentsCopy[rowIndex][fieldName].value = value
        changeData(tableContentsCopy, stateKey);
    };

    const onChangeTextData = (
        value: any,
        fieldName: string,
        rowIndex: number,
    ) => {
        const tableContentsCopy = createCopyOfTableContents(tableContents);
        tableContentsCopy[rowIndex][fieldName].value = value
        changeData(tableContentsCopy, stateKey);
    }

    const onChangeExcludeData = (
    value: any,
    fieldName: string,
    rowIndex: number,
    ) => {
        const tableContentsCopy = createCopyOfTableContents(tableContents);
        tableContentsCopy[rowIndex][fieldName].value = value;
        changeData(tableContentsCopy, stateKey);
    };

    const lookupOptionsAndOnChangeFunctions = {
        musicUsage: {
            options: musicUsageOptions,
            onChangeValue: onChangeMusicUsageDropDownData,
        },
        duration: {
            onChangeValue: onChangeDurationData,
        },
        sideTrackSequence: {
            onChangeValue: onChangeSequenceData,
        },
        isrc: {
            onChangeValue: onChangeTextData,
        },
        proprietaryID: {
            onChangeValue: onChangeTextData,
        },
        order: {
            onChangeValue: onChangeOrder,
        },
        exclude: {
            onChangeValue: onChangeExcludeData,
        },
    };

    const rowActions = {
        iconButton: {
            icon: Open16Regular,
            renderTitleAsLink: true,
            onClick: rowAction,
        }            
    }

    return  <div>
            <DisplayTable
                tableContents={tableContentsWithSideTrackAndSequence}
                dataGridTableData={filteredTableData}
                showRowNumber={false}
                rowActions={rowActions}
                removeRowAction={removeRowAction}
                sortTableByColumn={(section) => sortTableByColumn(section)}
                activeSortHeaderSection={activeSortHeaderSection}
                lookupOptionsAndOnChangeFunctions={lookupOptionsAndOnChangeFunctions}
                isRenderLinkTitle={true}
                addCopiedProductWorks={addCopiedProductWorks}
            
            />
            <div className="tableContainer">
                <SizedActionButton
                    buttonText={dataGridTableData.fields.find(f => f.name === SEARCH_ACTION).data}
                    buttonAction={() => showModal(WORK_SEARCH_VIEW, PRODUCT_WORKS_KEY, {isDataSourceIntray: isDataSourceIntray}, true, 'Search')}
                />
                <SizedActionButton
                    buttonText={dataGridTableData.fields.find(f => f.name === COPY_WORKS_ACTION).data}
                    buttonAction={() => handleCopyProductWorks()}
                    disabled={isCopyDisabled || tableContentsWithSideTrackAndSequence.length === 0}
                />
                {tableContentsWithSideTrackAndSequence.length === 0 ?
                <SizedActionButton
                    buttonText={dataGridTableData.fields.find(f => f.name === PASTE_WORKS_ACTION).data}
                    buttonAction={() => addCopiedProductWorks(0)}
                    disabled={tableContentsWithSideTrackAndSequence.length !== 0}
                /> : null
                }
            </div>
        </div>
}

export default ProductMaintenanceWorkTableContainer;